import { BaseReportContext, ReportContext } from "@/app/features/reports/contexts/report-context";
import { useGetPublishedReportQuery } from "@app.raytd.com/store";
import React, { useCallback, useMemo, useState } from "react";
import { ReportEntity, ReportRevision } from "store/src/lib/services/types";
import { SelectedFilters } from "@/app/features/reports/types/filters";

/**
 * This provider is used to provide the report and revision to the report context.
 * It also sets the report id to the revision id which is expected by the usePublishedReportData hook.
 */

const PublishedReportProvider = ({ revisionId, children }: { revisionId: number, children: React.ReactNode }) => {

    const {
        data: publishedReportData,
        isLoading: isDataLoading,
        error: publishedReportError,
      } = useGetPublishedReportQuery(Number(revisionId));

    const editMode = false;
    console.debug('PublishedReportProvider', publishedReportData);

    const handleSetEditMode = useCallback((editMode: boolean) => {
        // Published reports cannot be edited
        return;
    }, []);

    const [viewFiltersMap, setViewFiltersMap] = useState<Record<string, SelectedFilters>>({});

    const handleSetViewFilters = useCallback((viewId: string, filters: SelectedFilters | ((prev: SelectedFilters) => SelectedFilters)) => {
        setViewFiltersMap(prevMap => {
            const currentSelectedFilters = prevMap[viewId] || {} as SelectedFilters; // Provide default empty SelectedFilters
            const newSelectedFilters = typeof filters === 'function'
                ? filters(currentSelectedFilters)
                : filters;
            
            return {
                ...prevMap,
                [viewId]: newSelectedFilters
            };
        });
    }, []);

    const value: BaseReportContext = useMemo(() => ({
        report: {
            ...publishedReportData?.published?.report,
        },
        isLoading: isDataLoading,
        error: publishedReportError,
        isEditable: false,
        editMode,
        setEditMode: handleSetEditMode,
        revisionId: revisionId?.toString(),
        isPublished: true,
        publishedReportData: publishedReportData,
        permissions: publishedReportData?.permissions,
        revision: publishedReportData?.revision,
        viewFiltersMap: viewFiltersMap,
        setViewFilters: handleSetViewFilters
    }), [publishedReportData, editMode, handleSetEditMode, revisionId, viewFiltersMap, handleSetViewFilters]);

    if (!publishedReportData) {
        return null;
    }

    return <ReportContext.Provider
        value={value}
        key={revisionId?.toString()}
    >
        {children}
    </ReportContext.Provider>;
}

export default PublishedReportProvider;